import { type ApolloClient, gql } from '@apollo/client/core';
import type { Query } from '../../generated/graphql';

/**
 * The offer sheet's two **rendering** fields, on the legacy connection.
 *
 * Everything else about the offer sheet has moved to AppSchema and lives in `api/App/Offer`.
 * These two have not: AppSchema's `Sheet` carries neither `htmlContent` nor `pdfDownloadUrl`,
 * and the builder needs both -- the preview pane renders the first and the download button opens
 * the second -- so the migration would otherwise have shipped a visible regression.
 *
 * They live here, in the legacy namespace, rather than beside the rest of the offer sheet in
 * `App/`, because `App/` is defined by its client: `api-layer.md`'s table reads
 * `App/` => `gqlApiClient`, and a legacy `apiClient` call inside it would be a lie about which
 * schema that directory speaks to. Splitting the feature across two namespaces is the smaller
 * cost, and the split is exactly as temporary as the backend gap.
 *
 * **Delete this module** once the fields are published on AppSchema; the selection folds back into
 * `App/Offer`'s `SHEET_FIELDS` and this whole directory goes.
 *
 * Two things in `@vp/admin` go with it, and neither compiles without this module:
 * `composables/useOfferSheetRendering.ts`, which resolves the legacy client on the offer-sheet
 * store's behalf, and the store's own `reloadRendering()` action -- along with its
 * `renderingReloading` state, its re-entrancy guard, and the `finally` call every write makes to
 * it. Once `htmlContent` rides back on the mutation payload the way `summary` already does, an
 * offer-sheet write needs no follow-up read at all.
 */

export const OFFER_SHEET_RENDERING_QUERY = gql`
  query offerSheetRendering($id: Int!) {
    offerSheet(id: $id) {
      htmlContent
      pdfDownloadUrl
    }
  }
`;

export type OfferSheetRendering = {
  htmlContent?: string;
  pdfDownloadUrl?: string;
};

export const fetchOfferSheetRendering = async (
  client: ApolloClient<Query>,
  id: number,
): Promise<OfferSheetRendering> => {
  const { data } = await client.query<Query>({
    query: OFFER_SHEET_RENDERING_QUERY,
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  const sheet = data.offerSheet;

  if (!sheet) {
    return {};
  }

  return {
    htmlContent: sheet.htmlContent ?? undefined,
    pdfDownloadUrl: sheet.pdfDownloadUrl ?? undefined,
  };
};
